import { PROFILES, type PerformanceProfile, type PerformanceProfileName } from './PerformanceProfile';

/** Signaux matériels bruts — collectés d'un côté, classés de l'autre (testable). */
export interface DeviceSignals {
  memoryGB?: number;
  cores?: number;
  pixelCount: number;
  gpuRenderer?: string;
  isMobile: boolean;
}

export function collectSignals(): DeviceSignals {
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    memoryGB: nav.deviceMemory,
    cores: navigator.hardwareConcurrency,
    pixelCount: screen.width * screen.height * (window.devicePixelRatio || 1) ** 2,
    gpuRenderer: readGpuRenderer(),
    isMobile: /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent),
  };
}

function readGpuRenderer(): string | undefined {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');
    if (!gl || !('getExtension' in gl)) return undefined;
    const ctx = gl as WebGLRenderingContext;
    const info = ctx.getExtension('WEBGL_debug_renderer_info');
    if (!info) return undefined;
    return String(ctx.getParameter(info.UNMASKED_RENDERER_WEBGL));
  } catch {
    return undefined;
  }
}

const LOW_END_GPU = /mali-4|mali-t|adreno [1-4]\d\d|adreno 50[0-6]|powervr|videocore/i;
const HIGH_END_GPU = /apple gpu|apple a1[4-9]|apple m[1-9]|adreno 7|adreno 6[5-9]|immortalis|mali-g7[1-9]|mali-g[89]/i;

/** Classification pure : facile à tester unitairement. */
export function classifySignals(s: DeviceSignals): PerformanceProfileName {
  let score = 0;

  if (s.memoryGB !== undefined) score += s.memoryGB >= 8 ? 2 : s.memoryGB >= 4 ? 1 : -2;
  if (s.cores !== undefined) score += s.cores >= 8 ? 2 : s.cores >= 6 ? 1 : s.cores <= 4 ? -1 : 0;

  if (s.gpuRenderer) {
    if (HIGH_END_GPU.test(s.gpuRenderer)) score += 2;
    else if (LOW_END_GPU.test(s.gpuRenderer)) score -= 3;
  }

  // Écran très dense sur mobile = plus de pixels à remplir
  if (s.isMobile && s.pixelCount > 6_000_000) score -= 1;
  if (!s.isMobile) score += 1;

  if (score >= 3) return 'high';
  if (score <= -2) return 'low';
  return 'medium';
}

export function detectProfile(): PerformanceProfile {
  return PROFILES[classifySignals(collectSignals())];
}
